<template>
  <div class="account-info-ctn">
    <h2 class="header-text">{{ $t('profile.menu.account_information') }}</h2>
    <v-container class="mt-6 ma-0">
      <v-form ref="form" v-model="validForm">
        <v-row class="gutter-1">
          <v-col cols="12" md="6">
            <small class="field-label">{{ $t('profile.company_name') }}</small> <br>
            <span class="field-value">{{ profile.company?.name }}</span>
          </v-col>
          <v-col cols="12" md="6">
            <small class="text-capitalize field-label">{{ $t('profile.email') }}</small> <br>
            <span class="field-value">{{ profile.email }}</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              dense
              outlined
              v-model="firstname"
              :rules="[formRules.required, formRules.noMarkup, formRules.maxLength(150)]"
              :counter="150"
              maxlength="150"
              :label="`${$t('profile.first_name')}*`"
              required/>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              dense
              outlined
              v-model="lastname"
              :rules="[formRules.required, formRules.noMarkup, formRules.maxLength(150)]"
              :counter="150"
              maxlength="150"
              :label="`${$t('profile.last_name')}*`"
              required/>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              dense
              outlined
              v-model="website"
              :rules="[formRules.required, formRules.websiteRule, formRules.maxLength(2000)]"
              :counter="2000"
              maxlength="2000"
              @blur="onBlurUrl"
              @focus="onFocusUrl"
              :label="`${$t('profile.company_website')}*`"
              required></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              dense
              outlined
              v-model="selectedCountry"
              :items="countryList"
              :label="$t('profile.country')"
              :search-input.sync="originSearchInput"
              @change="originSearchInput = ''"
              item-text="description"
              item-value="key"
            >
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <ChangePasswordModal />
          </v-col>
          <v-col md="6" class="text-end">
            <v-btn class="px-11" :loading="loading" :disabled="!validForm" @click="submit" color="primary">
              {{ $t('profile.update') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-5">
          <v-col cols="12" class="mb-0 pb-0">
            <h2 class="header-text mb-4">{{ $t('profile.connected_account') }}</h2>
          </v-col>
          <v-col cols="12" class="mt-0 pt-0">
            <div v-if="isGoogleLinked" key="google-linked" class="d-flex align-center flex-wrap connected-status">
              <google-icon class="mr-3" />
              <span class="field-value">{{ $t('profile.google_connected') }}</span>
              <v-btn text color="error" :loading="unlinkingGoogle" @click="showUnlinkDialog = true" class="google-unlink-btn ml-md-4">
                {{ $t('profile.google_unlink') }}
              </v-btn>
            </div>
            <div v-else key="google-unlinked" class="google-signin-ctn">
              <div class="google-btn-shell" :class="{ 'google-btn-shell-disabled': linkingGoogle }">
                <button type="button" class="google-sso-btn" :disabled="linkingGoogle" @click="onGoogleShellClick">
                  <google-icon class="google-sso-icon" />
                  <span class="google-sso-label">{{ $t('profile.log_in_with_google') }}</span>
                </button>
                <!-- transparent real Google button overlaid to capture the click and return an id_token -->
                <div ref="googleBtn" class="google-sdk-overlay" aria-hidden="true"></div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-5">
          <v-col cols="12" md="6">
            <h2 class="title text-capitalize">{{ $t('profile.system_settings') }}</h2>
            <LanguageSelect />
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-dialog v-model="showUnlinkDialog" width="550" persistent>
      <v-card>
        <v-card-title>{{ $t('profile.google_unlink_confirm_title') }}</v-card-title>
        <v-card-text class="py-5 px-5 black--text">{{ $t('profile.google_unlink_confirm_message') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="unlinkingGoogle" @click="showUnlinkDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="error" :loading="unlinkingGoogle" @click="unlinkGoogle">
            {{ $t('profile.google_unlink') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import countries from '@/const/countries'
import ChangePasswordModal from '@/components/profile/ChangePasswordModal.vue'
import LanguageSelect from '@/components/LanguageSelect.vue'
import GoogleIcon from '@/components/icons/Google.vue'
import Api from '@/api.js'

export default {
  name: 'AccountInformation',
  components: {
    LanguageSelect,
    ChangePasswordModal,
    GoogleIcon
  },
  data: () => ({
    validForm: false,
    firstname: '',
    lastname: '',
    website: '',
    title: '',
    originSearchInput: '',
    selectedCountry: null,
    countries: countries,
    showPasswordPopup: false,
    loading: false,
    linkingGoogle: false,
    googleInitialized: false,
    unlinkingGoogle: false,
    showUnlinkDialog: false
  }),
  watch: {
    isGoogleLinked (linked) {
      if (!linked) {
        this.$nextTick(() => this.renderGoogleButton())
      }
    }
  },
  computed: {
    ...mapGetters(['profile']),
    isGoogleLinked () {
      return this.profile?.external_logins?.some(login => login.provider === 'google') ?? false
    },
    formRules () {
      return {
        required: v => !!(v && String(v).trim()) || this.$t('field_required'),
        noMarkup: v => !v || !/[<>]/.test(v) || this.$t('profile.invalid_characters'),
        maxLength: max => v => (!v || v.length <= max) || this.$t('profile.max_length_error', { max }),
        websiteRule: v => (/^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(v) || !v || !v.length) || this.$t('profile.website_alert')
      }
    },
    countryList () {
      const locale = this.$i18n.locale
      const capitalize = s => s ? s.charAt(0).toLocaleUpperCase(locale) + s.slice(1) : s
      return countries
        .map(c => ({ key: c.key, description: capitalize(this.$t(`countries_${c.key}`)) }))
        .sort((a, b) => a.description.localeCompare(b.description, locale))
    }
  },
  methods: {
    loadGoogleScript () {
      return new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = resolve
        script.onerror = () => reject(new Error('Failed to load Google Sign-In script'))
        document.head.appendChild(script)
      })
    },

    async renderGoogleButton () {
      const container = this.$refs.googleBtn
      if (!container) return
      try {
        await this.loadGoogleScript()
        if (!this.googleInitialized) {
          window.google.accounts.id.initialize({
            client_id: Api.google_sso_client_id,
            callback: (response) => this.handleGoogleCredential(response),
            ux_mode: 'popup',
            auto_select: false
          })
          this.googleInitialized = true
        }
        container.innerHTML = ''
        // fall back to 213px if not laid out yet, so --gsi-scale never becomes 0.
        const shellWidth = container.offsetWidth || 221
        const renderWidth = Math.min(shellWidth, 400)
        container.style.setProperty('--gsi-scale', (shellWidth / renderWidth).toFixed(4))
        window.google.accounts.id.renderButton(container, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: renderWidth
        })
      } catch (e) {
        this.$store.dispatch('showSnackBar', { message: this.$t('profile.google_link_error_generic') })
      }
    },

    async handleGoogleCredential (response) {
      if (this._isDestroyed) return
      this.linkingGoogle = true
      try {
        await this.$store.dispatch('linkGoogleAccount', response.credential)
        this.$store.dispatch('showSnackBar', { message: this.$t('profile.google_link_success') })
      } catch (error) {
        const errorCode = error?.response?.data?.error_code
        const messageMap = {
          'social.invalid_token': this.$t('profile.google_link_error_invalid_token'),
          'social.email_mismatch': this.$t('profile.google_link_error_email_mismatch'),
          'social.already_linked': this.$t('profile.google_link_error_already_linked'),
          'sso.auth_required': this.$t('profile.google_link_error_sso_required')
        }
        this.$store.dispatch('showSnackBar', { message: messageMap[errorCode] || this.$t('profile.google_link_error_generic') })
      } finally {
        this.linkingGoogle = false
      }
    },

    onGoogleShellClick () {
      if (!window.google?.accounts?.id) return
      if (window.matchMedia('(pointer: coarse)').matches) {
        window.google.accounts.id.prompt()
      }
    },

    async unlinkGoogle () {
      this.unlinkingGoogle = true
      try {
        await this.$store.dispatch('unlinkGoogleAccount')
        this.showUnlinkDialog = false
        this.$store.dispatch('showSnackBar', { message: this.$t('profile.google_unlink_success') })
      } catch (error) {
        const message = error?.response?.status === 409
          ? this.$t('profile.google_unlink_error_disabled')
          : this.$t('profile.google_unlink_error_generic')
        this.$store.dispatch('showSnackBar', { message })
      } finally {
        this.unlinkingGoogle = false
      }
    },

    onFocusUrl () {
      if (!this.website) {
        this.website = 'https://'
      }
    },
    onBlurUrl () {
      if (this.website && this.website === 'https://') {
        this.website = ''
      }
    },
    resetForm (user) {
      if (!user) return
      this.firstname = user.first_name
      this.lastname = user.last_name
      this.website = user.company?.website
      this.title = user.title
      this.selectedCountry = user.company?.country
      this.$nextTick(() => {
        if (!this.$refs.form) return
        if (this.$refs.form.validate()) {
          this.$refs.form.resetValidation()
        }
      })
    },

    async submit () {
      const valid = this.$refs.form ? this.$refs.form.validate() : this.validForm
      if (!valid) {
        return this.$store.dispatch('showSnackBar', { message: this.$t('profile.account_info_error') })
      }
      this.firstname = (this.firstname || '').trim()
      this.lastname = (this.lastname || '').trim()
      this.website = (this.website || '').trim()
      this.loading = true
      try {
        await this.$store.dispatch('updateUserProfile', {
          first_name: this.firstname,
          last_name: this.lastname,
          title: this.title
        })
        await this.$store.dispatch('updateCompanyInfo', {
          slug: this.profile?.company?.slug,
          website: this.website,
          country: this.selectedCountry
        })
        await this.$store.dispatch('loadUserProfile')
        await this.$store.dispatch('showSnackBar', { message: this.$t('profile.account_info_updated') })
      } catch (e) {
        await this.$store.dispatch('showSnackBar', { message: this.$t('profile.account_info_error') })
      } finally {
        this.loading = false
      }
    }
  },
  beforeDestroy () {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel()
    }
  },

  async created () {
    try {
      this.resetForm(await this.$store.dispatch('loadUserProfile'))
    } catch (e) {
      // ignore: the Google button renders in mounted() regardless.
    }
  },

  activated () {
    this.resetForm(this.profile)
  },

  mounted () {
    // render here so a failed profile fetch can't leave the button dead.
    if (!this.isGoogleLinked) {
      this.$nextTick(() => this.renderGoogleButton())
    }
  }
}
</script>

<style lang="sass" scoped>
@import "@/variables.sass"
@mixin small-label
  font-style: normal
  font-weight: 400
  line-height: 16px
  letter-spacing: 0.4px

.account-info-ctn
  padding-bottom: 40px

  .field-label
    @include small-label
    color: rgba(0, 0, 0, 0.6)
    font-size: 12px

  .field-value
    @include small-label
    color: rgba(0, 0, 0, 0.87)
    font-size: 16px

  .header-text
    color: #242424
    font-size: 26px
    font-style: normal
    font-weight: 500
    line-height: 24px

  .connected-status
    font-size: 16px
    color: rgba(0, 0, 0, 0.87)

  .google-signin-ctn
    width: 221px
    max-width: 100%

  .google-btn-shell
    position: relative
    width: 100%
    height: 39px
    &:hover .google-sso-btn
      background: #f7f8f8
      border-color: #d5d7da

  .google-btn-shell-disabled
    pointer-events: none
    opacity: 0.7

  .google-sso-btn
    display: flex
    align-items: center
    justify-content: flex-start
    gap: 12px
    width: 100%
    height: 39px
    box-sizing: border-box
    padding: 6px 16px
    border-radius: 8px
    border: 1px solid #d5d7da
    background: #fff
    box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05)
    cursor: pointer
    user-select: none
    font: inherit
    color: inherit
    -webkit-appearance: none
    appearance: none
    &:focus
      outline: 1px solid #15bbf0
      outline-offset: 1px
    &[disabled]
      cursor: wait
      opacity: 0.7

  .google-sso-icon
    width: 24px
    height: 24px
    flex-shrink: 0
    display: block
    margin: 0

  .google-sso-label
    color: #414651
    font-family: 'Inter', 'Roboto', sans-serif
    font-size: 16px
    font-weight: 600
    line-height: 24px
    white-space: nowrap
    text-align: left

  .google-sdk-overlay
    position: absolute
    inset: 0
    opacity: 0
    overflow: hidden
    z-index: 2
    display: flex
    align-items: center
    justify-content: center
    iframe
      transform: scaleX(var(--gsi-scale, 1))
      transform-origin: center

  .google-unlink-btn
    text-transform: uppercase
    letter-spacing: 0.8px
</style>
